import type { WidgetChannel, WidgetChannelType } from './types';
import { isWidgetChannelType } from './types';

const DISPLAY_NAMES: Record<WidgetChannelType, string> = {
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  messenger: 'Messenger',
  instagram: 'Instagram',
  webchat: 'WebChat',
};

const WEBCHAT_FALLBACK: WidgetChannel = {
  type: 'webchat',
  enabled: true,
  destination: null,
  displayName: 'WebChat',
  icon: 'webchat',
};

/**
 * Normalizes the public channels payload into a safe, display-ready list.
 * Legacy WebChat embeds still ensure WebChat remains available unless
 * `{ fallbackWebchat: false }` is passed (growth widgets).
 */
export function resolveWidgetChannels(
  channels: unknown,
  options: { fallbackWebchat?: boolean } = {},
): WidgetChannel[] {
  const fallbackWebchat = options.fallbackWebchat !== false;
  if (!Array.isArray(channels)) {
    return fallbackWebchat ? [WEBCHAT_FALLBACK] : [];
  }

  const resolved: WidgetChannel[] = [];
  const seen = new Set<string>();

  for (const entry of channels) {
    const channel = normalizeChannel(entry);
    if (!channel || !channel.enabled) continue;
    if (channel.available === false) continue;
    if (
      channel.type === 'webchat' &&
      !channel.webchatPublicKey &&
      !fallbackWebchat
    ) {
      continue;
    }
    const key = channel.id ?? channel.type;
    if (seen.has(key)) continue;
    if (channel.type !== 'webchat' && !channel.destination) continue;
    seen.add(key);
    resolved.push(channel);
  }

  // An explicit channel list is authoritative. Only inject WebChat when the
  // payload omitted channels entirely (legacy embeds).
  if (fallbackWebchat && channels.length === 0) {
    return [WEBCHAT_FALLBACK];
  }
  if (fallbackWebchat) {
    const withoutWebchat = resolved.filter((c) => c.type !== 'webchat');
    const webchat = resolved.find((c) => c.type === 'webchat');
    return webchat ? [...withoutWebchat, webchat] : resolved;
  }

  return resolved;
}

export function hasExternalChannels(channels: WidgetChannel[]): boolean {
  return channels.some(
    (channel) => channel.type !== 'webchat' && channel.enabled,
  );
}

export function openChannelDestination(destination: string): void {
  const open = globalThis.open?.bind(globalThis);
  if (typeof open === 'function') {
    open(destination, '_blank', 'noopener,noreferrer');
  }
}

function normalizeChannel(value: unknown): WidgetChannel | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  const rawType =
    typeof record.type === 'string' ? record.type.trim().toLowerCase() : '';
  if (!isWidgetChannelType(rawType)) {
    return null;
  }

  const enabled = record.enabled !== false;
  const destination =
    typeof record.destination === 'string' && record.destination.trim()
      ? record.destination.trim()
      : null;

  if (destination && !isSafeHttpUrl(destination)) {
    return null;
  }

  // Always project onto the public shape — never spread unknown API fields
  // (botToken, ciphertext, webhook secrets, etc. must never reach the browser).
  return {
    ...(typeof record.id === 'string' && record.id.trim()
      ? { id: record.id.trim() }
      : {}),
    type: rawType,
    enabled,
    available: record.available !== false,
    destination: rawType === 'webchat' ? null : destination,
    displayName:
      typeof record.displayName === 'string' && record.displayName.trim()
        ? record.displayName.trim()
        : DISPLAY_NAMES[rawType],
    icon:
      typeof record.icon === 'string' && record.icon.trim()
        ? record.icon.trim()
        : rawType,
    ...(typeof record.webchatPublicKey === 'string' &&
    record.webchatPublicKey.trim()
      ? { webchatPublicKey: record.webchatPublicKey.trim() }
      : {}),
  };
}

function isSafeHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}
